import { AfterViewInit, Component, Input, OnInit, } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { DiscountType } from 'src/app/common/enums/discount-type';
import { BillType } from 'src/app/common/enums/bill-type';
import { environment } from 'src/environments/environment';
import { FallbackImageDirective } from 'src/app/common/directive/fallback-image.directive';
import { HttpClient } from '@angular/common/http';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { firstValueFrom } from 'rxjs';
import { UserService } from '../../user/user.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';

@Component({
    selector: 'app-bill-print',
    standalone: true,
    imports: [
        CommonModule,
        MaterialModule,
        MatTableModule,
        MatCardModule,
        IndianCurrencyPipe,
        TranslateModule,
        FallbackImageDirective
    ],
    providers: [
        IndianCurrencyPipe,
        DatePipe,
        DefaultValuePipe
    ],
    templateUrl: './bill-print.component.html'
})
export class BillPrintComponent implements OnInit, AfterViewInit {
    @Input() billData: any;
    @Input() doctorInfo: any;
    @Input() doctorId?: number;
    @Input() hospitalInfo: any;
    @Input() successResult: any;
    @Input() billType?: BillType;
    DiscountType = DiscountType;
    BillType = BillType;
    charges: any[] = [];
    today: Date = new Date();
    staticimagepath = environment.imagePath;

    constructor(
        private http: HttpClient,
        private hospitalState: HospitalStateService,
        private printSettingService: PrintSettingService,
        private datePipe: DatePipe,
        private indianCurrencyPipe: IndianCurrencyPipe,
        private userService: UserService,
        private defaultValuePipe: DefaultValuePipe
    ) { }

    ngOnInit(): void {
        this.charges = this.billData?.billDetails ?? [];
        if (this.isDoctorInfoIncomplete() && this.doctorId) {
            this.userService.getUserByID(this.doctorId).subscribe(res => {
                if (res.isSuccess && res.result) {
                    const user = res.result as any;
                    this.doctorInfo = {
                        id: user.id,
                        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
                        degree: user.degree,
                        specialityName: user.specialityName,
                        registrationNumber: user.registrationNumber,
                        phoneNumber: user.phoneNumber,
                        specialityId: user.specialityId,
                    };
                }
            });
        }
    }

    ngAfterViewInit(): void { }

    get groupedBillDetails(): { category: string, items: any[], subTotal: number }[] {
        if (!this.isIPDBill || !this.billData?.billDetails) return [];

        const grouped: Record<string, any[]> = {};
        this.billData.billDetails.forEach((element: any) => {
            const category = element.insuranceChargeCategory || 'Other Charges';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(element);
        });

        return Object.keys(grouped).map(key => {
            const items = grouped[key];
            const subTotal = items.reduce((sum, item) => sum + (item.total || item.totalCharge || 0), 0);
            return {
                category: key,
                items: items,
                subTotal: subTotal
            };
        });
    }

    get isIPDBill(): boolean {
        return this.billType === BillType.IPDBill || !!this.billData?.ipdNumber;
    }

    get discountSymbol(): string {
        const discountType = DiscountType[this.billData?.discountType as keyof typeof DiscountType];
        if (discountType === DiscountType.FixedAmount) {
            return '₹';
        }
        return '%';
    }

    private isDoctorInfoIncomplete(): boolean {  
        return !this.doctorInfo || !this.doctorInfo.registrationNumber || !this.doctorInfo.phoneNumber;  
    } 

    async createPrintContent(): Promise<string> {
        try {
            let template = await firstValueFrom(
                this.http.get('assets/print-template.html', { responseType: 'text' })
            ) || '';

            const hospitalSafe = this.hospitalState.getHospital();
            const settings = await this.printSettingService.getPrintSettingPromise(hospitalSafe.id);

            // Format charges table(s)
            let allTablesHTML = '';

            if (this.isIPDBill) {
                // Group by insuranceChargeCategory for IPD Bills
                const groupedDetails: Record<string, any[]> = {};
                (this.billData?.billDetails || []).forEach((element: any) => {
                    const category = element.insuranceChargeCategory || 'Other Charges';
                    if (!groupedDetails[category]) {
                        groupedDetails[category] = [];
                    }
                    groupedDetails[category].push(element);
                });

                let groupIndex = 1;
                for (const category of Object.keys(groupedDetails)) {
                    const groupItems = groupedDetails[category];
                    let groupTableRows = '';
                    let groupSubTotal = 0;

                    groupItems.forEach((element: any, i: number) => {
                        const total = element.total || element.totalCharge || 0;
                        groupSubTotal += total;
                        groupTableRows += `
                            <tr>
                                <td style="border: 1px solid #ccc; padding: 8px;">${i + 1}</td>
                                <td class="text-left" style="border: 1px solid #ccc; padding: 8px;">${element.chargeName}</td>
                                <td style="border: 1px solid #ccc; padding: 8px;">${element.quantity}</td>
                                <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(element.rate)}</td>
                                <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(total)}</td>
                            </tr>
                        `;
                    });

                    allTablesHTML += `
                        <div class="print-table-wrapper" style="margin-bottom: 20px;">
                            <h3 style="margin: 0 0 5px 0; font-size: 16px; background-color: #e9ecef; padding: 5px; border: 1px solid #ccc; border-bottom: none;">${category}</h3>
                            <table class="table table-bordered" style="width: 100%; border-collapse: collapse;">
                                <thead>
                                    <tr style="background-color: #f2f2f2;">
                                        <th style="border: 1px solid #ccc; padding: 8px; text-align: left; width: 5%;">No.</th>
                                        <th style="border: 1px solid #ccc; padding: 8px; text-align: left; width: 45%;">Particulars</th>
                                        <th style="border: 1px solid #ccc; padding: 8px; text-align: left; width: 10%;">Qty</th>
                                        <th style="border: 1px solid #ccc; padding: 8px; text-align: right; width: 20%;">Rate</th>
                                        <th style="border: 1px solid #ccc; padding: 8px; text-align: right; width: 20%;">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${groupTableRows}
                                </tbody>
                                <tfoot>
                                    <tr style="background-color: #f8f9fa; font-weight: bold;">
                                        <td colspan="4" style="border: 1px solid #ccc; padding: 8px; text-align: right;">Sub Total</td>
                                        <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(groupSubTotal)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    `;
                    groupIndex++;
                }
            } else {
                // Flat table for OPD
                let tableRows = '';
                (this.billData?.billDetails || []).forEach((element: any, i: number) => {
                    const total = element.total || element.totalCharge || 0;
                    tableRows += `
                        <tr>
                            <td style="border: 1px solid #ccc; padding: 8px;">${i + 1}</td>
                            <td class="text-left" style="border: 1px solid #ccc; padding: 8px;">${element.chargeName}</td>
                            <td style="border: 1px solid #ccc; padding: 8px;">${element.quantity}</td>
                            <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(element.rate)}</td>
                            <td style="border: 1px solid #ccc; padding: 8px; text-align: right;">${this.indianCurrencyPipe.transform(total)}</td>
                        </tr>
                    `;
                });

                allTablesHTML = `
                    <div class="print-table-wrapper" style="margin-bottom: 20px;">
                        <table class="table table-bordered" style="width: 100%; border-collapse: collapse;">
                            <thead>
                                <tr style="background-color: #f2f2f2;">
                                    <th style="border: 1px solid #ccc; padding: 8px; text-align: left; width: 5%;">No.</th>
                                    <th style="border: 1px solid #ccc; padding: 8px; text-align: left; width: 45%;">Charge</th>
                                    <th style="border: 1px solid #ccc; padding: 8px; text-align: left; width: 10%;">Qty</th>
                                    <th style="border: 1px solid #ccc; padding: 8px; text-align: right; width: 20%;">Rate</th>
                                    <th style="border: 1px solid #ccc; padding: 8px; text-align: right; width: 20%;">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${tableRows}
                            </tbody>
                        </table>
                    </div>
                `;
            }

            // Wait for doctor info if needed
            if ((!this.doctorInfo || !this.doctorInfo.registrationNumber || !this.doctorInfo.phoneNumber) && this.doctorId) {
                const res = await firstValueFrom(this.userService.getUserByID(this.doctorId));
                if (res.isSuccess && res.result) {
                    const user = res.result as any;
                    this.doctorInfo = {
                        id: user.id,
                        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
                        degree: user.degree,
                        registrationNumber: user.registrationNumber,
                        phoneNumber: user.phoneNumber,
                        specialityId: user.specialityId,
                        specialityName: user.specialityName,

                    };
                }
            }

            const billNo = this.successResult?.billNo || this.billData?.billNo || '';
            const billDate = this.datePipe.transform(this.successResult?.billDate || this.billData?.billDate, 'dd-MM-yyyy') || '';
            const opdNo = this.billData?.opdNumber || '';
            const opdDate = this.datePipe.transform(this.billData?.opdDate, 'dd-MM-yyyy') || '';
            const ipdNo = this.billData?.ipdNumber || '';
            const admissionDate = this.datePipe.transform(this.billData?.admissionDate, 'dd-MM-yyyy') || '';
            const gender = this.billData?.gender === 'M' ? 'Male' : 'Female';

            const billDetailsCenter = `
                    <div style="text-align:center;margin-bottom:10px;">
                        <h2 style="margin:0 0 10px 0;text-transform:uppercase;text-decoration:underline;font-size:18px;">
                            Bill Receipt
                        </h2>
                    </div>

                    ${this.isIPDBill ? `
                    <table style="width:100%; font-size:14px; border-collapse:collapse; border: 1px solid #ccc;margin-bottom:5px;">
                        <tr>
                            <td style="width:50%; vertical-align:top; padding: 10px 20px 10px 10px;">
                                <strong>Patient Name :</strong> ${this.billData?.patientName || ''}<br>
                                <strong>Relative Name :</strong> ${this.billData?.relativeName || ''}<br>
                                <strong>Gender / Age :</strong> ${gender} / ${this.billData?.age || ''}<br>
                                <strong>Date Of Birth :</strong> ${this.datePipe.transform(this.billData?.dob, 'dd-MM-yyyy') || ''}<br>
                                ${this.billData?.mobileNumber ? 
                                `<strong>Mobile Number :</strong> ${this.billData.mobileNumber}<br>` : ''}

                                ${this.billData?.address ? 
                                `<strong>Address :</strong> ${this.billData.address}<br>` : ''}
                                <strong>Doctor Name :</strong> ${this.doctorInfo?.name || ''}
                            </td>
                            <td style="width:50%; vertical-align:top; text-align: right; padding: 10px 10px 10px 20px;">
                                <strong>Bill No :</strong> ${billNo}<br>
                                <strong>Bill Date :</strong> ${billDate}<br>    
                                <strong>IPD No :</strong> ${ipdNo}<br>
                                <strong>Admission Date :</strong> ${admissionDate}<br>
                                <strong>Discharge Date :</strong> ${this.datePipe.transform(this.billData?.dischargeDate, 'dd-MM-yyyy') || ''}<br>
                                <strong>Bed No :</strong> ${this.billData?.bedNo || ''}
                            </td>
                        </tr>
                    </table>
                    ` : ``}

            ${!this.isIPDBill ? `
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 12px; background-color: #f2f2f2; border: 1px solid #ccc;">
                    <tr>
                        <td style="padding: 5px; text-align: left; width: 33.33%;"><strong>Bill No:</strong> ${billNo}</td>
                        <td style="padding: 5px; text-align: left; width: 33.33%;"><strong>Bill Date:</strong> ${billDate}</td>
                        <td style="padding: 5px; text-align: left; width: 33.33%;"><strong>Patient Name:</strong> ${this.billData?.patientName}</td>
                    </tr>
                    <tr>
                        <td style="padding: 5px; text-align: left;"><div><strong>OPD No:</strong> ${opdNo}</div></td>
                        <td style="padding: 5px; text-align: left;"> <div><strong>OPD Date:</strong> ${opdDate}</div>
                         ${this.billData?.mobileNumber 
                            ? `<td style="padding: 5px; text-align: left;"><strong>Mobile:</strong> ${this.billData.mobileNumber}</td>` 
                            : `<td style="padding: 5px;"></td>`
                        }
                        </td>
                    </tr>
                </table>` : ``
                }
            `;

            const sectionContent = `
                ${billDetailsCenter}
                ${allTablesHTML}
                <div class="summary-totals" style="text-align: right; margin-top: 20px; font-size: 14px;">
                    <p style="margin: 5px 0;"><strong>Sub Total:</strong> ${this.indianCurrencyPipe.transform(this.billData?.subTotal)}</p>
                    <p style="margin: 5px 0;"><strong>Discount (${this.discountSymbol}):</strong> ${this.billData?.discount}</p>
                    <div style="margin-top: 10px; font-size: 18px;">
                        <strong>Total:</strong> ${this.indianCurrencyPipe.transform(this.billData?.grandTotal)}
                    </div>
                </div>
            `;

            const fullImagePath = hospitalSafe.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
            const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

            const replacements: Record<string, string> = {
                reportTitle: 'Bill Receipt',
                hospitalName: this.defaultValuePipe.transform(hospitalSafe.name, 'Hospital Name'),
                hospitalAddress: this.defaultValuePipe.transform(hospitalSafe.address, ''),
                phone: this.defaultValuePipe.transform(hospitalSafe.phone, ''),
                reportSections: sectionContent,
                doctorName: this.defaultValuePipe.transform(this.doctorInfo?.name, ''),
                qualification: this.defaultValuePipe.transform(this.doctorInfo?.degree, ''),
                doctorRegistrationNo: this.doctorInfo?.registrationNumber 
                    ? `Reg No. ${this.defaultValuePipe.transform(this.doctorInfo?.registrationNumber, '')}` 
                    : '',
                doctorPhone: this.doctorInfo?.phoneNumber 
                    ? `M. ${this.defaultValuePipe.transform(this.doctorInfo?.phoneNumber, '')}` 
                    : '',
                logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
                patientName: this.defaultValuePipe.transform(this.billData?.patientName, ''),
                patientAge: '',
                opdNumber: opdNo,
                opdDate: opdDate
            };

            let filledTemplate = template;
            for (const key in replacements) {
                const value = replacements[key] ?? '';
                const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
                filledTemplate = filledTemplate.replace(regex, value);
            }

            // Apply settings
            if (settings) {
                const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
                const headerClass = settings.logoPosition === 'center' ? 'center' : '';
                const headerAlignment = settings.addressAlignment || 'left';

                const styles = `
                    :root {
                        --logo-size: ${settings.logoSize || '60px'};
                        --logo-position: ${logoPos};
                        --header-alignment: ${headerAlignment};
                        --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                        --address-font-size: ${settings.addressFontSize || 13}px;
                        --footer-display: ${settings.showFooter ? 'block' : 'none'};
                        --doctor-signature-top-display: ${this.isIPDBill ? 'none' : (settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none')};
                        --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
                    }
                    .table-bordered th, .table-bordered td { border: 1px solid #ccc !important; }
                `;
                filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
            } else {
                filledTemplate = filledTemplate.replace('{{headerClass}}', '');
            }

            return filledTemplate;
        } catch (error) {
            console.error('Error creating bill print content:', error);
            throw error;
        }
    }
}